import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Icon, ThemeToggle, LogoMark, type IconName } from '@/components';
import { useAuth } from '@/auth/AuthProvider';
import { useIsDesktop } from '@/hooks/useIsDesktop';

interface NavItem {
  to: string;
  icon: IconName;
  key: string;
  end?: boolean;
}

const TABS: NavItem[] = [
  { to: '/learn', icon: 'home', key: 'nav.home', end: true },
  { to: '/learn/catalog', icon: 'book', key: 'nav.catalog' },
  { to: '/learn/learning', icon: 'stack', key: 'nav.learning' },
  { to: '/learn/wallet', icon: 'award', key: 'nav.wallet' },
  { to: '/learn/profile', icon: 'user', key: 'nav.profile' },
];

/** Routes where the mobile tab bar hides for a full-screen, immersive flow. */
const IMMERSIVE = [/\/enroll$/, /\/lesson\//, /\/test\//];

/** Switches between the two distinct learner experiences. */
export function LearnerLayout() {
  const isDesktop = useIsDesktop();
  return isDesktop ? <DesktopChrome /> : <MobileChrome />;
}

/* ── Desktop: top-nav web console ── */
function DesktopChrome() {
  const { t } = useTranslation();
  const { user, role, signOut } = useAuth();
  const navigate = useNavigate();
  // Staff with console access get a way back (nav by permission, not a role switch).
  const consolePath = role === 'instructor' ? '/teach' : role === 'admin' || role === 'superadmin' ? '/admin' : null;

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--paper)', display: 'flex', flexDirection: 'column' }}>
      <header
        style={{
          height: 62,
          flex: 'none',
          padding: '0 32px',
          borderBottom: '1px solid var(--line)',
          background: 'var(--card)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 40,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
          <button onClick={() => navigate('/learn')} style={{ display: 'flex', alignItems: 'center', gap: 10, border: 'none', background: 'none', cursor: 'pointer' }}>
            <LogoMark size={30} />
            <span style={{ fontSize: 15.5, fontWeight: 600, letterSpacing: '-.01em' }}>Warm Paper</span>
          </button>
          <nav style={{ display: 'flex', gap: 4 }}>
            {TABS.slice(0, 4).map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} className="wp-desknav">
                {t(it.key)}
              </NavLink>
            ))}
          </nav>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {consolePath && (
            <button onClick={() => navigate(consolePath)} className="btn btn-ghost btn-sm">
              <Icon name="shield" size={15} /> Console
            </button>
          )}
          <ThemeToggle />
          <button onClick={() => navigate('/learn/profile')} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
            <Avatar initials={user?.initials ?? 'SC'} size={34} tone={user?.tone} photoUrl={user?.photoUrl} />
          </button>
          <button onClick={() => { signOut(); navigate('/welcome'); }} title="Sign out" aria-label="Sign out" className="btn-quiet" style={{ padding: 9, borderRadius: 99, border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--ink-3)' }}>
            <Icon name="logout" size={19} />
          </button>
        </div>
      </header>
      <main style={{ flex: 1, minHeight: 0 }}>
        <Outlet />
      </main>
    </div>
  );
}

/* ── Mobile: stack-navigated app with bottom tabs ── */
function MobileChrome() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const immersive = IMMERSIVE.some((re) => re.test(pathname));

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--paper)', display: 'flex', flexDirection: 'column' }}>
      <main style={{ flex: 1, minHeight: 0, paddingBottom: immersive ? 0 : 84 }}>
        <Outlet />
      </main>

      {!immersive && (
        <nav
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 40,
            background: 'var(--card)',
            borderTop: '1px solid var(--line)',
            padding: '10px 8px calc(12px + env(safe-area-inset-bottom))',
            display: 'grid',
            gridTemplateColumns: 'repeat(5,1fr)',
          }}
        >
          {TABS.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} className="wp-tab">
              {({ isActive }) => (
                <>
                  <span className="wp-tab-ico"><Icon name={it.icon} size={22} stroke={isActive ? 2 : 1.6} /></span>
                  <span style={{ fontSize: 10.5, fontWeight: isActive ? 600 : 500 }}>{t(it.key)}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
